import { useEffect, useState } from "react";
import apiClient, { formatApiError } from "../api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import MediaPicker from "../components/MediaPicker";
import { Save } from "lucide-react";
import { toast } from "sonner";

const SettingsPage = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/settings").then(({ data }) => setForm(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.updated_at;
      const { data } = await apiClient.patch("/admin/settings", payload);
      setForm(data);
      toast.success("Instellingen opgeslagen");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-sm text-zinc-500">Laden...</div>;

  return (
    <div data-testid="admin-settings-page" className="pb-20">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
            CMS · Instellingen
          </p>
          <h1 className="font-display text-5xl text-white tracking-tight">
            Site-instellingen
          </h1>
          <p className="mt-3 text-sm text-zinc-400 font-body max-w-xl">
            Algemene gegevens die in de header, footer en op meerdere pagina's
            verschijnen.
          </p>
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="settings-save"
          className="inline-flex items-center gap-2 px-6 py-3 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold disabled:opacity-60"
        >
          <Save size={12} /> {saving ? "Opslaan..." : "Opslaan"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 flex flex-col gap-6">
          <Section title="Bedrijfsgegevens">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Bedrijfsnaam">
                <Input
                  data-testid="settings-company-name"
                  value={form.company_name || ""}
                  onChange={(e) => set("company_name", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Tagline">
                <Input
                  value={form.tagline || ""}
                  onChange={(e) => set("tagline", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Telefoonnummer">
                <Input
                  data-testid="settings-phone"
                  value={form.phone || ""}
                  onChange={(e) => set("phone", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="E-mailadres">
                <Input
                  data-testid="settings-email"
                  value={form.email || ""}
                  onChange={(e) => set("email", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Adres" className="md:col-span-2">
                <Textarea
                  value={form.address || ""}
                  onChange={(e) => set("address", e.target.value)}
                  rows={2}
                  className="bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none resize-none"
                />
              </Field>
              <Field label="Openingstijden">
                <Input
                  value={form.opening_hours || ""}
                  onChange={(e) => set("opening_hours", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
            </div>
          </Section>

          <Section title="Sociale media">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Instagram URL">
                <Input
                  value={form.instagram_url || ""}
                  onChange={(e) => set("instagram_url", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="LinkedIn URL">
                <Input
                  value={form.linkedin_url || ""}
                  onChange={(e) => set("linkedin_url", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="WhatsApp URL of nummer">
                <Input
                  value={form.whatsapp_url || ""}
                  onChange={(e) => set("whatsapp_url", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
            </div>
          </Section>

          <Section title="Footer & CTA">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Footer tekst" className="md:col-span-2">
                <Textarea
                  value={form.footer_text || ""}
                  onChange={(e) => set("footer_text", e.target.value)}
                  rows={3}
                  className="bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none resize-none"
                />
              </Field>
              <Field label="Standaard CTA tekst">
                <Input
                  value={form.default_cta_text || ""}
                  onChange={(e) => set("default_cta_text", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Standaard CTA link">
                <Input
                  value={form.default_cta_link || ""}
                  onChange={(e) => set("default_cta_link", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                />
              </Field>
            </div>
          </Section>
        </div>

        <aside className="lg:col-span-4 flex flex-col gap-6">
          <Section title="Branding">
            <div className="flex flex-col gap-5">
              <MediaPicker
                label="Logo (optioneel)"
                value={form.logo_id}
                onChange={(id) => set("logo_id", id)}
              />
              <MediaPicker
                label="Favicon (optioneel)"
                value={form.favicon_id}
                onChange={(id) => set("favicon_id", id)}
              />
            </div>
          </Section>
        </aside>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="bg-ink-900 border border-white/[0.06] p-7">
    <h2 className="font-display text-2xl text-white mb-6 tracking-tight">
      {title}
    </h2>
    {children}
  </section>
);

const Field = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-2 ${className}`}>
    <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
      {label}
    </span>
    {children}
  </label>
);

export default SettingsPage;
